/** Avatar color safety — never render invisible white/near-white chips. */

function clampByte(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)))
}

/** Parse #rgb / #rrggbb; returns null for CSS vars / named / invalid. */
export function parseHexColor(input: string | null | undefined): string | null {
  if (!input) return null
  const s = input.trim()
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s)
  if (!m) return null
  let h = m[1]
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  return `#${h.toLowerCase()}`
}

export function relativeLuminance(hex: string): number {
  const h = parseHexColor(hex)
  if (!h) return 0.2
  const r = parseInt(h.slice(1, 3), 16) / 255
  const g = parseInt(h.slice(3, 5), 16) / 255
  const b = parseInt(h.slice(5, 7), 16) / 255
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

function darkenHex(hex: string, factor: number): string {
  const h = parseHexColor(hex)
  if (!h) return hex
  const r = parseInt(h.slice(1, 3), 16)
  const g = parseInt(h.slice(3, 5), 16)
  const b = parseInt(h.slice(5, 7), 16)
  return `#${[r, g, b]
    .map((c) => clampByte(c * (1 - factor)).toString(16).padStart(2, '0'))
    .join('')}`
}

const MAX_BG_LUM = 0.72

/**
 * Prefer user color when contrast-safe; otherwise darken or fall back.
 * Non-hex (CSS vars) pass through unchanged.
 */
export function sanitizeAvatarColor(
  input: string | null | undefined,
  fallback: string,
): string {
  const hex = parseHexColor(input)
  if (!hex) return input?.trim() || fallback
  let out = hex
  let lum = relativeLuminance(out)
  if (lum <= MAX_BG_LUM) return out
  for (let i = 0; i < 6 && lum > MAX_BG_LUM; i++) {
    out = darkenHex(out, 0.22)
    lum = relativeLuminance(out)
  }
  if (lum > MAX_BG_LUM) {
    const fb = parseHexColor(fallback)
    return fb || '#6d28d9'
  }
  return out
}

export function avatarFgForBg(bg: string): string {
  const hex = parseHexColor(bg)
  if (!hex) return '#ffffff'
  return relativeLuminance(hex) > 0.55 ? '#1a1d27' : '#ffffff'
}

export function needsAvatarOutline(bg: string): boolean {
  const hex = parseHexColor(bg)
  if (!hex) return false
  return relativeLuminance(hex) > 0.55
}
